<template>
    <Head title="Add Corrective Actions" />

    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div class="flex items-center space-x-4">
                <Link
                    :href="route('incidents.show', incident.id)"
                    class="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                </Link>
                <div>
                    <h2 class="text-2xl font-semibold text-gray-900 dark:text-white">
                        Add Corrective Actions
                    </h2>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Incident #{{ incident.incident_number }}: {{ incident.title }}
                    </p>
                </div>
            </div>
        </div>

        <!-- Info Alert -->
        <div class="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-lg">
            <div class="flex items-start">
                <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="ml-3">
                    <p class="text-sm text-blue-800 dark:text-blue-300">
                        Add one or more corrective actions, assign a person who will handle them, and set a due date.
                        Click Submit when done to change incident status to Pending Corrective Action, or Cancel to go back without saving.
                    </p>
                </div>
            </div>
        </div>

        <form @submit.prevent="submitActions" class="space-y-6">
            <!-- Assignment Section -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Assignment Details</h3>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <!-- Assign To -->
                    <div>
                        <label for="assigned_to" class="block text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
                            Assign To <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="form.corrective_action_assigned_to"
                            id="assigned_to"
                            class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        >
                            <option value="">Select User...</option>
                            <option v-for="user in users" :key="user.id" :value="user.id">
                                {{ user.name }}
                            </option>
                        </select>
                        <p v-if="form.errors.corrective_action_assigned_to" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.corrective_action_assigned_to }}
                        </p>
                    </div>

                    <!-- Due Date -->
                    <div>
                        <label for="due_date" class="block text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
                            Due Date <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            v-model="form.corrective_action_due_date"
                            type="date"
                            id="due_date"
                            class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            required
                        />
                        <p v-if="form.errors.corrective_action_due_date" class="mt-1 text-sm text-red-600 dark:text-red-400">
                            {{ form.errors.corrective_action_due_date }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Actions List -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Corrective Actions</h3>
                    <span class="text-sm text-gray-600 dark:text-gray-400">{{ form.actions.length }} action(s) added</span>
                </div>

                <!-- Added Actions -->
                <div v-if="form.actions.length > 0" class="space-y-4 mb-6">
                    <div
                        v-for="(action, index) in form.actions"
                        :key="index"
                        class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50"
                    >
                        <div class="flex justify-between items-start">
                            <div class="flex-1">
                                <div class="flex items-center space-x-2 mb-2">
                                    <span class="text-xs font-semibold px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                        {{ getActionTypeLabel(action.action_type) }}
                                    </span>
                                    <span class="text-xs text-gray-500 dark:text-gray-400">Action #{{ index + 1 }}</span>
                                </div>
                                <h4 class="font-medium text-gray-900 dark:text-white mb-1">{{ action.title }}</h4>
                                <p class="text-sm text-gray-600 dark:text-gray-400">{{ action.description }}</p>
                            </div>
                            <button
                                type="button"
                                @click="removeAction(index)"
                                class="ml-4 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                title="Remove Action"
                            >
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Add Action Form -->
                <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 class="font-medium text-gray-900 dark:text-white">Add New Action</h4>

                    <!-- Action Type -->
                    <div>
                        <label for="action_type" class="block text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
                            Action Type <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <select
                            v-model="newAction.action_type"
                            id="action_type"
                            class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="">Select Type...</option>
                            <option value="corrective">Corrective Action</option>
                            <option value="preventive">Preventive Action</option>
                        </select>
                    </div>

                    <!-- Title -->
                    <div>
                        <label for="action_title" class="block text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
                            Action Title <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            v-model="newAction.title"
                            type="text"
                            id="action_title"
                            class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Enter action title"
                        />
                    </div>

                    <!-- Description -->
                    <div>
                        <label for="action_description" class="block text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">
                            Description <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <textarea
                            v-model="newAction.description"
                            id="action_description"
                            rows="3"
                            class="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Describe what needs to be done"
                        ></textarea>
                    </div>

                    <button
                        type="button"
                        @click="addAction"
                        :disabled="!isNewActionValid"
                        class="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-500 text-white text-sm font-semibold rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                        </svg>
                        Add Action to List
                    </button>
                </div>

                <p v-if="form.errors.actions" class="mt-2 text-sm text-red-600 dark:text-red-400">
                    {{ form.errors.actions }}
                </p>
            </div>

            <!-- Form Actions -->
            <div class="flex items-center justify-end space-x-4 bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                <Link
                    :href="route('incidents.show', incident.id)"
                    class="inline-flex items-center px-6 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 text-sm font-semibold rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                    Cancel
                </Link>
                <button
                    type="submit"
                    :disabled="form.processing || form.actions.length === 0"
                    class="inline-flex items-center px-6 py-2.5 bg-blue-600 dark:bg-blue-500 text-white text-sm font-semibold rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ form.processing ? 'Submitting...' : 'Submit Corrective Actions' }}
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, useForm, usePage } from '@inertiajs/vue3';
import route from '@/Utils/route';

const page = usePage();

const props = defineProps({
    incident: Object,
    users: Array,
});

const form = useForm({
    corrective_action_assigned_to: '',
    corrective_action_due_date: '',
    actions: [],
    _company_id: page.props.company_id,
});

const newAction = ref({
    action_type: '',
    title: '',
    description: '',
});

const isNewActionValid = computed(() => {
    return newAction.value.action_type &&
           newAction.value.title.trim() &&
           newAction.value.description.trim();
});

const getActionTypeLabel = (type) => {
    const labels = {
        'corrective': 'Corrective Action',
        'preventive': 'Preventive Action',
        'quick_fix': 'Quick Fix',
    };
    return labels[type] || type;
};

const addAction = () => {
    if (!isNewActionValid.value) return;

    form.actions.push({
        action_type: newAction.value.action_type,
        title: newAction.value.title,
        description: newAction.value.description,
    });

    // Reset form
    newAction.value = {
        action_type: '',
        title: '',
        description: '',
    };
};

const removeAction = (index) => {
    form.actions.splice(index, 1);
};

const submitActions = () => {
    if (form.actions.length === 0) {
        alert('Please add at least one corrective action before submitting.');
        return;
    }

    if (!form.corrective_action_assigned_to || !form.corrective_action_due_date) {
        alert('Please fill in both assigned person and due date.');
        return;
    }

    form.post(route('incidents.actions.submit', props.incident.id), {
        preserveScroll: true,
        onSuccess: () => {
            // Redirect handled by controller
        },
    });
};
</script>
